import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Save, Pencil, GripVertical, Check, Send, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { MD_COMPONENTS } from "@/lib/markdown";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

function emptyProduct(type) {
  return {
    type, title: "", subtitle: "", author: "", instructor: "", price: 0,
    cover_image: "", description: "", category: "Strategy", badge: "",
    creator_bio: "", creator_picture: "",
    preview_chapters: [], pages: 0, modules: [],
  };
}

export default function Admin() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [creatingPost, setCreatingPost] = useState(null);

  const reload = async () => {
    setLoading(true);
    const [p, o, s, i, pl, fn] = await Promise.all([
      api.get("/products"),
      api.get("/admin/orders").catch(() => ({ data: [] })),
      api.get("/admin/subscribers").catch(() => ({ data: [] })),
      api.get("/admin/speaking").catch(() => ({ data: [] })),
      api.get("/admin/payment-plans").catch(() => ({ data: [] })),
      api.get("/admin/blog").catch(() => ({ data: [] })),
    ]);
    setProducts(p.data); setOrders(o.data); setSubscribers(s.data);
    setInquiries(i.data); setPaymentPlans(pl.data); setPosts(fn.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const exportCSV = (rows, columns, filename) => {
    if (!rows.length) return;
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [columns.map((c) => c.label).join(",")]
      .concat(rows.map((r) => columns.map((c) => escape(r[c.key])).join(",")))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const remove = async (pid) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${pid}`);
    toast.success("Deleted");
    reload();
  };

  const save = async (p) => {
    try {
      if (p.product_id) {
        const { product_id, ...rest } = p;
        await api.put(`/admin/products/${product_id}`, rest);
      } else {
        await api.post("/admin/products", p);
      }
      toast.success("Saved");
      setEditing(null); setCreating(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  // ---- 3-Pay Plans admin actions ----
  const grantPlanAccess = async (req) => {
    const confirm = window.confirm(
      `Grant ${req.name || req.email} full access to Forged in Fire?\n\n` +
      `This creates a placeholder account if they don't have one, adds the course to their library, and emails them a sign-in link.\n\n` +
      `Use this only after you've confirmed the first $349 payment has cleared.`
    );
    if (!confirm) return;
    try {
      const { data } = await api.post(`/admin/payment-plans/${req.request_id}/grant`);
      toast.success(`Access granted to ${data.email}. Confirmation email sent.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not grant access");
    }
  };

  // ---- Field Notes (blog) admin actions ----
  // `opts.autoBroadcast` triggers the newsletter broadcast immediately after
  // a successful save — used by the "Broadcast on publish" checkbox in the dialog.
  const savePost = async (post, opts = {}) => {
    try {
      let slug = post.slug;
      if (post._existing_slug) {
        const { _existing_slug, ...rest } = post;
        await api.put(`/admin/blog/${_existing_slug}`, rest);
        slug = _existing_slug;
        toast.success("Field Note updated");
      } else {
        await api.post("/admin/blog", post);
        toast.success("Field Note created");
      }
      setEditingPost(null); setCreatingPost(null);

      if (opts.autoBroadcast && post.published) {
        try {
          const { data } = await api.post(`/admin/blog/${slug}/broadcast`);
          toast.success(`Broadcasting to ${data.recipient_count} subscriber${data.recipient_count === 1 ? "" : "s"}`);
        } catch (e) {
          toast.error(`Saved, but broadcast failed: ${e?.response?.data?.detail || e.message}`);
        }
      }

      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const deletePost = async (slug) => {
    if (!window.confirm("Delete this Field Note?")) return;
    try {
      await api.delete(`/admin/blog/${slug}`);
      toast.success("Deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  const broadcastPost = async (post) => {
    if (!post.published) {
      toast.error("Publish the post before broadcasting");
      return;
    }
    if (!window.confirm(`Send "${post.title}" to all newsletter subscribers right now?`)) return;
    try {
      const { data } = await api.post(`/admin/blog/${post.slug}/broadcast`);
      toast.success(`Broadcasting to ${data.recipient_count} subscriber${data.recipient_count === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    }
  };

  // Silent autosave for the modules field — used after drag-reorder inside the dialog.
  // Returns the persisted product (with new module/lesson ids) so the dialog can resync.
  const persistModules = async (productId, modules) => {
    const { data } = await api.put(`/admin/products/${productId}`, { modules });
    return data;
  };

  return (
    <Layout>
      <section className="border-b border-brand-line">
        <div className="container-narrow py-12">
          <span className="overline text-brand-rust">Admin Console</span>
          <h1 className="font-display text-5xl mt-3 text-brand-ink">Catalog & Orders</h1>
        </div>
      </section>

      <section className="container-narrow py-10">
        <Tabs defaultValue="catalog" className="w-full">
          <TabsList className="bg-white border border-brand-line rounded-none p-0 h-auto flex-wrap">
            <TabsTrigger value="catalog" data-testid="tab-catalog" className="rounded-none px-6 py-3 data-[state=active]:bg-brand-ink data-[state=active]:text-white">Catalog</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none px-6 py-3 data-[state=active]:bg-brand-ink data-[state=active]:text-white">Orders</TabsTrigger>
            <TabsTrigger value="subscribers" data-testid="tab-subscribers" className="rounded-none px-6 py-3 data-[state=active]:bg-brand-ink data-[state=active]:text-white">Subscribers</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries" className="rounded-none px-6 py-3 data-[state=active]:bg-brand-ink data-[state=active]:text-white">Speaking</TabsTrigger>
            <TabsTrigger value="payment-plans" data-testid="tab-payment-plans" className="rounded-none px-6 py-3 data-[state=active]:bg-brand-ink data-[state=active]:text-white">3-Pay Plans</TabsTrigger>
            <TabsTrigger value="field-notes" data-testid="tab-field-notes" className="rounded-none px-6 py-3 data-[state=active]:bg-brand-ink data-[state=active]:text-white">Field Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="catalog">
            <div className="flex items-center justify-between my-6">
              <p className="overline text-brand-inkMuted">{products.length} products</p>
              <div className="flex gap-2">
                <button data-testid="admin-new-book" onClick={() => setCreating(emptyProduct("book"))} className="inline-flex items-center gap-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-4 py-2 text-sm">
                  <Plus className="w-4 h-4" /> New Book
                </button>
                <button data-testid="admin-new-course" onClick={() => setCreating(emptyProduct("course"))} className="inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-black px-4 py-2 text-sm">
                  <Plus className="w-4 h-4" /> New Course
                </button>
              </div>
            </div>
            <div className="bg-white border border-brand-line">
              <table className="w-full text-sm">
                <thead className="overline text-brand-inkMuted">
                  <tr className="border-b border-brand-line">
                    <th className="text-left p-4">Product</th>
                    <th className="text-left p-4">Type</th>
                    <th className="text-left p-4">Category</th>
                    <th className="text-left p-4">Price</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? <tr><td colSpan={5} className="p-8 text-center overline text-brand-inkMuted">Loading…</td></tr> :
                    products.map((p) => (
                      <tr key={p.product_id} className="border-b border-brand-line" data-testid={`admin-row-${p.product_id}`}>
                        <td className="p-4 flex items-center gap-3">
                          <img src={p.cover_image} alt="" className="w-10 h-12 object-cover" />
                          <div>
                            <div className="text-brand-ink font-medium">{p.title}</div>
                            <div className="text-xs text-brand-inkMuted">{p.author || p.instructor}</div>
                          </div>
                        </td>
                        <td className="p-4 text-brand-inkMuted capitalize">{p.type}</td>
                        <td className="p-4 text-brand-inkMuted">{p.category}</td>
                        <td className="p-4 text-brand-ink">${Number(p.price).toFixed(2)}</td>
                        <td className="p-4 text-right">
                          <button data-testid={`admin-edit-${p.product_id}`} onClick={() => setEditing(p)} className="text-brand-ink hover:text-brand-rust mr-3"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`admin-delete-${p.product_id}`} onClick={() => remove(p.product_id)} className="text-brand-inkMuted hover:text-brand-rust"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="bg-white border border-brand-line mt-6">
              <table className="w-full text-sm">
                <thead className="overline text-brand-inkMuted">
                  <tr className="border-b border-brand-line">
                    <th className="text-left p-4">Session</th>
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Items</th>
                    <th className="text-left p-4">Amount</th>
                    <th className="text-left p-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? <tr><td colSpan={5} className="p-8 text-center overline text-brand-inkMuted" data-testid="orders-empty">No orders yet.</td></tr> :
                    orders.map((o) => (
                      <tr key={o.session_id} className="border-b border-brand-line">
                        <td className="p-4 text-xs text-brand-inkMuted">{o.session_id.slice(0, 14)}…</td>
                        <td className="p-4">{o.email || "—"}</td>
                        <td className="p-4 text-brand-inkMuted">{o.items?.length} item(s)</td>
                        <td className="p-4 text-brand-ink">${Number(o.amount).toFixed(2)}</td>
                        <td className="p-4">
                          <span className={`overline ${o.payment_status === "paid" ? "text-brand-rust" : "text-brand-inkMuted"}`}>{o.payment_status}</span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="subscribers">
            <div className="flex items-center justify-between my-6">
              <p className="overline text-brand-inkMuted">{subscribers.length} subscribers</p>
              <button
                data-testid="export-subscribers-csv"
                disabled={!subscribers.length}
                onClick={() => exportCSV(
                  subscribers,
                  [{key:"email",label:"email"},{key:"name",label:"name"},{key:"source",label:"source"},{key:"created_at",label:"created_at"}],
                  "subscribers.csv",
                )}
                className="inline-flex items-center gap-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-4 py-2 text-sm disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
            <div className="bg-white border border-brand-line">
              <table className="w-full text-sm">
                <thead className="overline text-brand-inkMuted">
                  <tr className="border-b border-brand-line">
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Name</th>
                    <th className="text-left p-4">Source</th>
                    <th className="text-left p-4">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length === 0 ? (
                    <tr><td colSpan={4} className="p-8 text-center overline text-brand-inkMuted" data-testid="subscribers-empty">No subscribers yet.</td></tr>
                  ) : subscribers.map((s) => (
                    <tr key={s.subscriber_id} className="border-b border-brand-line" data-testid={`subscriber-row-${s.subscriber_id}`}>
                      <td className="p-4 text-brand-ink">{s.email}</td>
                      <td className="p-4 text-brand-inkMuted">{s.name || "—"}</td>
                      <td className="p-4 text-brand-inkMuted">{s.source || "—"}</td>
                      <td className="p-4 text-xs text-brand-inkMuted">{new Date(s.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries">
            <div className="flex items-center justify-between my-6">
              <p className="overline text-brand-inkMuted">{inquiries.length} speaking inquiries</p>
              <button
                data-testid="export-inquiries-csv"
                disabled={!inquiries.length}
                onClick={() => exportCSV(
                  inquiries,
                  [
                    {key:"name",label:"name"},{key:"email",label:"email"},
                    {key:"organization",label:"organization"},{key:"event_type",label:"event_type"},
                    {key:"event_date",label:"event_date"},{key:"audience_size",label:"audience_size"},
                    {key:"message",label:"message"},{key:"created_at",label:"created_at"},
                  ],
                  "speaking_inquiries.csv",
                )}
                className="inline-flex items-center gap-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-4 py-2 text-sm disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
            <div className="space-y-3">
              {inquiries.length === 0 ? (
                <div className="bg-white border border-brand-line p-12 text-center overline text-brand-inkMuted" data-testid="inquiries-empty">
                  No inquiries yet.
                </div>
              ) : inquiries.map((q) => (
                <div key={q.inquiry_id} className="bg-white border border-brand-line p-5" data-testid={`inquiry-row-${q.inquiry_id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <span className="overline text-brand-rust">{q.event_type || "inquiry"}</span>
                      <p className="font-serif text-xl text-brand-ink mt-1">{q.name} <span className="text-brand-inkMuted text-base font-sans"> · {q.organization || "—"}</span></p>
                      <p className="text-sm text-brand-inkMuted">{q.email} · {q.event_date || "no date"} · {q.audience_size || "audience: —"}</p>
                    </div>
                    <span className="text-xs text-brand-inkMuted">{new Date(q.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-4 text-brand-ink text-sm whitespace-pre-wrap leading-relaxed">{q.message}</p>
                  <a href={`mailto:${q.email}?subject=Re: speaking inquiry`} className="inline-block mt-4 overline text-brand-rust hover:text-brand-ink">Reply via email →</a>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="payment-plans">
            <div className="flex items-center justify-between my-6">
              <p className="overline text-brand-inkMuted" data-testid="payment-plans-count">{paymentPlans.length} payment-plan requests</p>
              <button
                data-testid="export-payment-plans-csv"
                disabled={!paymentPlans.length}
                onClick={() => exportCSV(
                  paymentPlans,
                  [
                    {key:"name",label:"name"},{key:"email",label:"email"},
                    {key:"phone",label:"phone"},{key:"course_title",label:"course_title"},
                    {key:"note",label:"note"},{key:"created_at",label:"created_at"},
                  ],
                  "payment_plans.csv",
                )}
                className="inline-flex items-center gap-2 border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-4 py-2 text-sm disabled:opacity-50"
              >
                Export CSV
              </button>
            </div>
            <div className="space-y-3">
              {paymentPlans.length === 0 ? (
                <div className="bg-white border border-brand-line p-12 text-center overline text-brand-inkMuted" data-testid="payment-plans-empty">
                  No payment-plan requests yet.
                </div>
              ) : paymentPlans.map((r) => {
                const status = r.status || "new";
                const statusLabel =
                  status === "granted" ? { text: "Granted", cls: "bg-brand-rust/10 text-brand-rust" } :
                  status === "stripe_initiated" ? { text: "Stripe started", cls: "bg-blue-50 text-blue-700" } :
                  status === "converted" ? { text: "Paid · Stripe", cls: "bg-brand-rust/10 text-brand-rust" } :
                  { text: "New · awaiting payment", cls: "bg-brand-bg text-brand-inkMuted" };
                return (
                <div key={r.request_id} className="bg-white border border-brand-line p-5" data-testid={`payment-plan-row-${r.request_id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <span className="overline text-brand-rust">3-Pay · {r.course_title || "Forged in Fire: The Course"}</span>
                      <p className="font-serif text-xl text-brand-ink mt-1">{r.name}</p>
                      <p className="text-sm text-brand-inkMuted">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-xs uppercase tracking-[.14em] px-2 py-1 ${statusLabel.cls}`} data-testid={`payment-plan-status-${r.request_id}`}>
                        {statusLabel.text}
                      </span>
                      <span className="text-xs text-brand-inkMuted">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {r.note && <p className="mt-4 text-brand-ink text-sm whitespace-pre-wrap leading-relaxed">{r.note}</p>}
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                    <button
                      onClick={() => grantPlanAccess(r)}
                      disabled={status === "granted" || status === "converted"}
                      data-testid={`payment-plan-grant-${r.request_id}`}
                      className="inline-flex items-center gap-1.5 bg-brand-ink text-white hover:bg-black px-3 py-1.5 text-xs font-medium tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title={status === "granted" ? "Already granted" : status === "converted" ? "Auto-granted via Stripe payment" : "Manually enroll this prospect"}
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                      {status === "granted" || status === "converted" ? "Access granted" : "Grant course access"}
                    </button>
                    <a href={`mailto:${r.email}?subject=Your 3-payment plan for Forged in Fire`} className="overline text-brand-rust hover:text-brand-ink" data-testid={`payment-plan-reply-${r.request_id}`}>Reply via email →</a>
                    <a href="https://dashboard.stripe.com/payment-links" target="_blank" rel="noopener noreferrer" className="overline text-brand-inkMuted hover:text-brand-ink">Open Stripe payment links →</a>
                  </div>
                  {r.granted_at && (
                    <p className="mt-3 text-xs text-brand-inkMuted">
                      Granted {new Date(r.granted_at).toLocaleString()}{r.granted_by ? ` · by ${r.granted_by}` : ""}
                    </p>
                  )}
                </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="field-notes">
            <div className="flex items-center justify-between my-6">
              <p className="overline text-brand-inkMuted" data-testid="field-notes-count">
                {posts.length} field note{posts.length === 1 ? "" : "s"}
              </p>
              <button
                data-testid="admin-new-field-note"
                onClick={() => setCreatingPost({
                  slug: "", title: "", excerpt: "", body: "", cover_image: "",
                  tag: "", author: "Robbie South", published: false,
                })}
                className="inline-flex items-center gap-2 bg-brand-ink text-white hover:bg-black px-4 py-2 text-sm"
              >
                <Plus className="w-4 h-4" /> New Field Note
              </button>
            </div>
            <div className="bg-white border border-brand-line">
              <table className="w-full text-sm">
                <thead className="overline text-brand-inkMuted">
                  <tr className="border-b border-brand-line">
                    <th className="text-left p-4">Title</th>
                    <th className="text-left p-4">Tag</th>
                    <th className="text-left p-4">Status</th>
                    <th className="text-left p-4">Published</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {posts.length === 0 ? (
                    <tr><td colSpan={5} className="p-8 text-center overline text-brand-inkMuted" data-testid="field-notes-empty">No field notes yet.</td></tr>
                  ) : posts.map((p) => (
                    <tr key={p.slug} className="border-b border-brand-line" data-testid={`field-note-row-${p.slug}`}>
                      <td className="p-4">
                        <div className="text-brand-ink font-medium">{p.title}</div>
                        <div className="text-xs text-brand-inkMuted">/{p.slug}</div>
                      </td>
                      <td className="p-4 text-brand-inkMuted">{p.tag || "—"}</td>
                      <td className="p-4">
                        {p.published ? (
                          <span className="overline text-brand-rust inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Published</span>
                        ) : (
                          <span className="overline text-brand-inkMuted inline-flex items-center gap-1"><EyeOff className="w-3 h-3" /> Draft</span>
                        )}
                      </td>
                      <td className="p-4 text-xs text-brand-inkMuted">{p.published_at ? new Date(p.published_at).toLocaleDateString() : "—"}</td>
                      <td className="p-4 text-right whitespace-nowrap">
                        <button
                          data-testid={`field-note-broadcast-${p.slug}`}
                          onClick={() => broadcastPost(p)}
                          disabled={!p.published}
                          title={p.published ? "Send to all subscribers" : "Publish before broadcasting"}
                          className="inline-flex items-center gap-1 text-brand-rust hover:text-brand-ink mr-4 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Send className="w-4 h-4" /> <span className="text-xs">Broadcast</span>
                        </button>
                        <button data-testid={`field-note-edit-${p.slug}`} onClick={() => setEditingPost({ ...p, _existing_slug: p.slug })} className="text-brand-ink hover:text-brand-rust mr-3"><Pencil className="w-4 h-4" /></button>
                        <button data-testid={`field-note-delete-${p.slug}`} onClick={() => deletePost(p.slug)} className="text-brand-inkMuted hover:text-brand-rust"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </section>

      <ProductDialog open={!!editing || !!creating} value={editing || creating} onClose={() => { setEditing(null); setCreating(null); }} onSave={save} onPersistModules={persistModules} />
      <FieldNoteDialog open={!!editingPost || !!creatingPost} value={editingPost || creatingPost} onClose={() => { setEditingPost(null); setCreatingPost(null); }} onSave={savePost} />
    </Layout>
  );
}

function ProductDialog({ open, value, onClose, onSave, onPersistModules }) {
  const [p, setP] = useState(value);
  const [drag, setDrag] = useState(null); // {kind:'module'|'lesson', mi, li?}
  const [autoSaveAt, setAutoSaveAt] = useState(null); // timestamp string when last autosaved
  useEffect(() => { setP(value); setDrag(null); setAutoSaveAt(null); }, [value]);
  if (!p) return null;
  const isCourse = p.type === "course";
  const set = (k, v) => setP({ ...p, [k]: v });

  // ---- Module / Lesson editor helpers (course only) ----
  const setModules = (mods) => setP({ ...p, modules: mods });
  const addModule = () =>
    setModules([...(p.modules || []), { title: "New module", lessons: [] }]);
  const updateModule = (mi, patch) => {
    const mods = [...(p.modules || [])];
    mods[mi] = { ...mods[mi], ...patch };
    setModules(mods);
  };
  const removeModule = (mi) =>
    setModules((p.modules || []).filter((_, i) => i !== mi));
  const addLesson = (mi) => {
    const mods = [...(p.modules || [])];
    const lessons = [...(mods[mi].lessons || []), { title: "New lesson", duration: "10 min", video_url: "", content: "" }];
    mods[mi] = { ...mods[mi], lessons };
    setModules(mods);
  };
  const updateLesson = (mi, li, patch) => {
    const mods = [...(p.modules || [])];
    const lessons = [...(mods[mi].lessons || [])];
    lessons[li] = { ...lessons[li], ...patch };
    mods[mi] = { ...mods[mi], lessons };
    setModules(mods);
  };
  const removeLesson = (mi, li) => {
    const mods = [...(p.modules || [])];
    mods[mi] = { ...mods[mi], lessons: (mods[mi].lessons || []).filter((_, i) => i !== li) };
    setModules(mods);
  };

  // Reorder helpers — autosave silently after drag-reorder if product exists
  const reorder = (arr, from, to) => {
    const next = [...arr];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    return next;
  };
  const persistAfterReorder = async (newModules) => {
    if (!p.product_id || !onPersistModules) return;
    try {
      const persisted = await onPersistModules(p.product_id, newModules);
      // Sync ids back from server
      if (persisted?.modules) {
        setP((prev) => ({ ...prev, modules: persisted.modules }));
      }
      const now = new Date();
      setAutoSaveAt(now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    } catch {
      toast.error("Autosave failed");
    }
  };
  const moveModule = (from, to) => {
    if (from === to) return;
    const next = reorder(p.modules || [], from, to);
    setModules(next);
    persistAfterReorder(next);
  };
  const moveLesson = (mi, from, to) => {
    if (from === to) return;
    const mods = [...(p.modules || [])];
    mods[mi] = { ...mods[mi], lessons: reorder(mods[mi].lessons || [], from, to) };
    setModules(mods);
    persistAfterReorder(mods);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{p.product_id ? "Edit" : "Create"} {p.type}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto p-1 space-y-8">
          {/* Base info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="overline text-brand-inkMuted">Title</Label>
              <Input data-testid="admin-input-title" value={p.title} onChange={(e) => set("title", e.target.value)} className="rounded-none mt-2" />
            </div>
            <div className="col-span-2">
              <Label className="overline text-brand-inkMuted">Subtitle</Label>
              <Input value={p.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className="rounded-none mt-2" />
            </div>
            <div>
              <Label className="overline text-brand-inkMuted">{isCourse ? "Instructor" : "Author"}</Label>
              <Input value={isCourse ? (p.instructor || "") : (p.author || "")} onChange={(e) => set(isCourse ? "instructor" : "author", e.target.value)} className="rounded-none mt-2" />
            </div>
            <div>
              <Label className="overline text-brand-inkMuted">Category</Label>
              <Input value={p.category} onChange={(e) => set("category", e.target.value)} className="rounded-none mt-2" />
            </div>
            <div>
              <Label className="overline text-brand-inkMuted">Price (USD)</Label>
              <Input type="number" step="0.01" value={p.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} className="rounded-none mt-2" />
            </div>
            <div>
              <Label className="overline text-brand-inkMuted">Badge (optional)</Label>
              <Input value={p.badge || ""} onChange={(e) => set("badge", e.target.value)} className="rounded-none mt-2" />
            </div>
            <div className="col-span-2">
              <Label className="overline text-brand-inkMuted">Cover image URL</Label>
              <Input value={p.cover_image} onChange={(e) => set("cover_image", e.target.value)} className="rounded-none mt-2" />
            </div>
            {!isCourse && (
              <div className="col-span-2">
                <Label className="overline text-brand-inkMuted">Amazon URL (per book)</Label>
                <Input
                  value={p.amazon_url || ""}
                  onChange={(e) => set("amazon_url", e.target.value)}
                  placeholder="https://amzn.to/..."
                  className="rounded-none mt-2"
                  data-testid="admin-input-amazon-url"
                />
                <p className="text-xs text-brand-inkMuted mt-1">The &ldquo;Buy on Amazon&rdquo; button on this book&apos;s page links to this URL.</p>
              </div>
            )}
            <div className="col-span-2">
              <Label className="overline text-brand-inkMuted">Description</Label>
              <Textarea rows={4} value={p.description} onChange={(e) => set("description", e.target.value)} className="rounded-none mt-2" />
            </div>
          </div>

          {/* Creator profile */}
          <div className="border-t border-brand-line pt-6">
            <p className="overline text-brand-rust mb-4">{isCourse ? "Instructor" : "Author"} profile</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="overline text-brand-inkMuted">Picture URL</Label>
                <Input data-testid="admin-input-creator-picture" value={p.creator_picture || ""} onChange={(e) => set("creator_picture", e.target.value)} className="rounded-none mt-2" />
              </div>
              <div className="col-span-2">
                <Label className="overline text-brand-inkMuted">Bio</Label>
                <Textarea data-testid="admin-input-creator-bio" rows={3} value={p.creator_bio || ""} onChange={(e) => set("creator_bio", e.target.value)} className="rounded-none mt-2" />
              </div>
            </div>
          </div>

          {/* Curriculum (course only) */}
          {isCourse && (
            <div className="border-t border-brand-line pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="overline text-brand-rust">Curriculum</p>
                  <p className="text-xs text-brand-inkMuted mt-1">
                    Drag the handle to reorder modules and lessons.
                    {autoSaveAt && (
                      <span className="ml-2 inline-flex items-center gap-1 text-brand-rust" data-testid="autosave-indicator">
                        <Check className="w-3 h-3" /> Saved at {autoSaveAt}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="admin-add-module"
                  onClick={addModule}
                  className="inline-flex items-center gap-2 text-sm border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white px-3 py-1.5"
                >
                  <Plus className="w-3 h-3" /> Add module
                </button>
              </div>

              <div className="space-y-4">
                {(p.modules || []).length === 0 && (
                  <p className="text-sm text-brand-inkMuted">No modules yet. Add the first one above.</p>
                )}
                {(p.modules || []).map((m, mi) => {
                  const isModuleDragOver = drag?.kind === "module" && drag.over === mi;
                  return (
                    <div
                      key={mi}
                      className={`border bg-brand-bg p-4 transition-colors ${isModuleDragOver ? "border-brand-rust" : "border-brand-line"}`}
                      data-testid={`admin-module-${mi}`}
                      onDragOver={(e) => {
                        if (drag?.kind === "module") {
                          e.preventDefault();
                          if (drag.over !== mi) setDrag({ ...drag, over: mi });
                        }
                      }}
                      onDrop={(e) => {
                        if (drag?.kind === "module") {
                          e.preventDefault();
                          moveModule(drag.from, mi);
                          setDrag(null);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          draggable
                          onDragStart={() => setDrag({ kind: "module", from: mi, over: mi })}
                          onDragEnd={() => setDrag(null)}
                          className="cursor-grab active:cursor-grabbing text-brand-inkMuted hover:text-brand-ink"
                          data-testid={`admin-module-handle-${mi}`}
                          title="Drag to reorder"
                        >
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <span className="overline text-brand-inkMuted">M0{mi + 1}</span>
                        <Input
                          value={m.title || ""}
                          onChange={(e) => updateModule(mi, { title: e.target.value })}
                          placeholder="Module title"
                          className="rounded-none flex-1 bg-white"
                          data-testid={`admin-module-title-${mi}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeModule(mi)}
                          className="text-brand-inkMuted hover:text-brand-rust"
                          data-testid={`admin-remove-module-${mi}`}
                        ><Trash2 className="w-4 h-4" /></button>
                      </div>

                      <div className="mt-4 space-y-3 pl-4 border-l border-brand-line">
                        {(m.lessons || []).map((l, li) => {
                          const isLessonDragOver =
                            drag?.kind === "lesson" && drag.mi === mi && drag.over === li;
                          return (
                            <div
                              key={li}
                              className={`bg-white border p-3 transition-colors ${isLessonDragOver ? "border-brand-rust" : "border-brand-line"}`}
                              data-testid={`admin-lesson-${mi}-${li}`}
                              onDragOver={(e) => {
                                if (drag?.kind === "lesson" && drag.mi === mi) {
                                  e.preventDefault();
                                  if (drag.over !== li) setDrag({ ...drag, over: li });
                                }
                              }}
                              onDrop={(e) => {
                                if (drag?.kind === "lesson" && drag.mi === mi) {
                                  e.preventDefault();
                                  moveLesson(mi, drag.from, li);
                                  setDrag(null);
                                }
                              }}
                            >
                              <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_120px_auto] gap-2 items-center">
                                <button
                                  type="button"
                                  draggable
                                  onDragStart={() => setDrag({ kind: "lesson", mi, from: li, over: li })}
                                  onDragEnd={() => setDrag(null)}
                                  className="cursor-grab active:cursor-grabbing text-brand-inkMuted hover:text-brand-ink"
                                  data-testid={`admin-lesson-handle-${mi}-${li}`}
                                  title="Drag to reorder"
                                >
                                  <GripVertical className="w-4 h-4" />
                                </button>
                                <Input
                                  value={l.title || ""}
                                  onChange={(e) => updateLesson(mi, li, { title: e.target.value })}
                                  placeholder="Lesson title"
                                  className="rounded-none"
                                />
                                <Input
                                  value={l.duration || ""}
                                  onChange={(e) => updateLesson(mi, li, { duration: e.target.value })}
                                  placeholder="Duration"
                                  className="rounded-none"
                                />
                                <button
                                  type="button"
                                  onClick={() => removeLesson(mi, li)}
                                  className="text-brand-inkMuted hover:text-brand-rust"
                                  data-testid={`admin-remove-lesson-${mi}-${li}`}
                                ><Trash2 className="w-4 h-4" /></button>
                              </div>
                              <Input
                                value={l.video_url || ""}
                                onChange={(e) => updateLesson(mi, li, { video_url: e.target.value })}
                                placeholder="Video URL (embed)"
                                className="rounded-none mt-2"
                              />
                              <Textarea
                                rows={2}
                                value={l.content || ""}
                                onChange={(e) => updateLesson(mi, li, { content: e.target.value })}
                                placeholder="Lesson description / notes"
                                className="rounded-none mt-2"
                              />
                            </div>
                          );
                        })}
                        <button
                          type="button"
                          onClick={() => addLesson(mi)}
                          className="inline-flex items-center gap-2 text-xs overline text-brand-rust hover:text-brand-ink"
                          data-testid={`admin-add-lesson-${mi}`}
                        >
                          <Plus className="w-3 h-3" /> Add lesson
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white">Cancel</button>
          <button data-testid="admin-save-product" onClick={() => onSave(p)} className="px-4 py-2 text-sm bg-brand-ink text-white hover:bg-black inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[''"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function FieldNoteDialog({ open, value, onClose, onSave }) {
  const [post, setPost] = useState(value);
  const [tab, setTab] = useState("write"); // write | preview
  const [autoBroadcast, setAutoBroadcast] = useState(false);
  const [wasPublished, setWasPublished] = useState(false);
  useEffect(() => {
    setPost(value);
    setTab("write");
    setAutoBroadcast(false);
    setWasPublished(!!(value && value.published));
  }, [value]);
  if (!post) return null;
  const isNew = !post._existing_slug;
  const set = (k, v) => setPost({ ...post, [k]: v });

  // Show the auto-broadcast checkbox only when this save will actually publish
  // for the first time (new post being created as published, OR a draft being
  // flipped to published). Re-broadcasting an already-published post requires
  // the row-level Broadcast button instead — prevents accidental duplicate sends.
  const willPublishNow = !!post.published && !wasPublished;

  const handleTitleChange = (v) => {
    if (isNew && (!post.slug || post.slug === slugify(post.title))) {
      setPost({ ...post, title: v, slug: slugify(v) });
    } else {
      setPost({ ...post, title: v });
    }
  };

  const submit = () => {
    if (!post.title?.trim()) return toast.error("Title is required");
    if (!post.slug?.trim()) return toast.error("Slug is required");
    if (!post.excerpt?.trim()) return toast.error("Excerpt is required");
    if (!post.body?.trim()) return toast.error("Body is required");
    if (!post.cover_image?.trim()) return toast.error("Cover image URL is required");
    onSave(post, { autoBroadcast: willPublishNow && autoBroadcast });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" data-testid="field-note-dialog-title">
            {isNew ? "New Field Note" : "Edit Field Note"}
          </DialogTitle>
          <DialogDescription className="text-sm text-brand-inkMuted">
            Write in Markdown. Toggle Preview to see how it will render on the public Field Notes page.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto p-1 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline text-brand-inkMuted">Title</Label>
              <Input
                data-testid="field-note-title"
                value={post.title || ""}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="rounded-none mt-1"
                placeholder="The Leadership Pressure Test"
              />
            </div>
            <div>
              <Label className="overline text-brand-inkMuted">Slug</Label>
              <Input
                data-testid="field-note-slug"
                value={post.slug || ""}
                onChange={(e) => set("slug", slugify(e.target.value))}
                disabled={!isNew}
                className="rounded-none mt-1 font-mono text-sm"
                placeholder="the-leadership-pressure-test"
              />
              {!isNew && <p className="text-xs text-brand-inkMuted mt-1">Slug locked after publish.</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline text-brand-inkMuted">Tag</Label>
              <Input
                data-testid="field-note-tag"
                value={post.tag || ""}
                onChange={(e) => set("tag", e.target.value)}
                className="rounded-none mt-1"
                placeholder="Leadership"
              />
            </div>
            <div>
              <Label className="overline text-brand-inkMuted">Author</Label>
              <Input
                data-testid="field-note-author"
                value={post.author || ""}
                onChange={(e) => set("author", e.target.value)}
                className="rounded-none mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="overline text-brand-inkMuted">Cover image URL</Label>
            <Input
              data-testid="field-note-cover"
              value={post.cover_image || ""}
              onChange={(e) => set("cover_image", e.target.value)}
              className="rounded-none mt-1 font-mono text-sm"
              placeholder="https://…"
            />
            {post.cover_image && (
              <img src={post.cover_image} alt="" className="mt-2 w-full max-h-48 object-cover border border-brand-line" />
            )}
          </div>

          <div>
            <Label className="overline text-brand-inkMuted">Excerpt (shown on Field Notes index + broadcast email)</Label>
            <Textarea
              data-testid="field-note-excerpt"
              value={post.excerpt || ""}
              onChange={(e) => set("excerpt", e.target.value)}
              rows={3}
              className="rounded-none mt-1"
              placeholder="A one- or two-sentence pull quote that pulls the reader in."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="overline text-brand-inkMuted">Body (Markdown)</Label>
              <div className="inline-flex border border-brand-line text-xs">
                <button
                  type="button"
                  data-testid="field-note-tab-write"
                  onClick={() => setTab("write")}
                  className={`px-3 py-1 ${tab === "write" ? "bg-brand-ink text-white" : "text-brand-ink"}`}
                >Write</button>
                <button
                  type="button"
                  data-testid="field-note-tab-preview"
                  onClick={() => setTab("preview")}
                  className={`px-3 py-1 border-l border-brand-line ${tab === "preview" ? "bg-brand-ink text-white" : "text-brand-ink"}`}
                >Preview</button>
              </div>
            </div>
            {tab === "write" ? (
              <Textarea
                data-testid="field-note-body"
                value={post.body || ""}
                onChange={(e) => set("body", e.target.value)}
                rows={18}
                className="rounded-none font-mono text-sm leading-relaxed"
                placeholder={"## A subheading\n\nA paragraph. **Bold** and *italic* and [a link](https://…) all work.\n\n- A bullet list\n- Another point\n\n> A pull quote feels like this."}
              />
            ) : (
              <div data-testid="field-note-preview" className="bg-white border border-brand-line p-6 max-w-none min-h-[300px]">
                <ReactMarkdown components={MD_COMPONENTS}>{post.body || "_Nothing to preview yet._"}</ReactMarkdown>
              </div>
            )}
            <p className="text-xs text-brand-inkMuted mt-2">
              Tip: <code>##</code> for headings, <code>**bold**</code>, <code>*italic*</code>,
              <code>&gt;</code> for quotes, <code>- </code> for bullets.
            </p>
          </div>

          <div className="border-t border-brand-line pt-4 space-y-3">
            <div className="flex items-center gap-3">
              <input
                id="fn-published"
                data-testid="field-note-published"
                type="checkbox"
                checked={!!post.published}
                onChange={(e) => set("published", e.target.checked)}
                className="w-4 h-4 accent-brand-rust"
              />
              <Label htmlFor="fn-published" className="text-sm text-brand-ink cursor-pointer">
                Publish (visible on /field-notes)
              </Label>
            </div>

            {willPublishNow && (
              <div className="flex items-start gap-3 bg-brand-bg border border-brand-line p-3" data-testid="auto-broadcast-block">
                <input
                  id="fn-auto-broadcast"
                  data-testid="field-note-auto-broadcast"
                  type="checkbox"
                  checked={autoBroadcast}
                  onChange={(e) => setAutoBroadcast(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-brand-rust"
                />
                <Label htmlFor="fn-auto-broadcast" className="text-sm text-brand-ink cursor-pointer leading-relaxed">
                  <span className="inline-flex items-center gap-2">
                    <Send className="w-3.5 h-3.5 text-brand-rust" />
                    Broadcast as newsletter immediately after publishing
                  </span>
                  <span className="block text-xs text-brand-inkMuted mt-1">
                    Sends excerpt + cover image + &ldquo;Read the full essay&rdquo; CTA to every subscriber. One-shot — uncheck if you want to broadcast later from the Field Notes table.
                  </span>
                </Label>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm border border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white">Cancel</button>
          <button data-testid="field-note-save" onClick={submit} className="px-4 py-2 text-sm bg-brand-ink text-white hover:bg-black inline-flex items-center gap-2">
            <Save className="w-4 h-4" /> Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
